import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as d3Geo from 'd3-geo';
import * as topojson from 'topojson-client';
import mexicoTopo from '../../assets/dashboard/mexico.json';

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SvgMap = styled.svg`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 10px rgba(0,0,0,0.3));
`;

const StatePath = styled.path`
  cursor: pointer;
  transition: all 0.2s ease;
  stroke: #1a1a1a;
  stroke-width: 0.5px;

  &:hover {
    filter: brightness(1.2);
    stroke-width: 1.5px;
    stroke: #fff;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: #fff;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  z-index: 10;
  border: 1px solid #BA955E;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  display: ${props => props.$visible ? 'block' : 'none'};
  left: ${props => props.$x}px;
  top: ${props => props.$y}px;
  transform: translate(-50%, -120%);
  max-width: 280px;

  .state-name {
    font-weight: 700;
    color: #BA955E;
    margin-bottom: 6px;
    font-size: 13px;
  }
  .profile-row {
    font-size: 11px;
    color: #e0e0e0;
    margin-bottom: 2px;
  }
`;

// Colores por perfil líder (según especificación)
const PROFILE_COLORS = {
  'Director Técnico': '#286EDD',
  'Dueño Equipo': '#286EDD',
  'Dueño del Equipo': '#286EDD',
  'Organizador de Liga': '#286EDD',
  'Organizador Liga': '#286EDD',
  'DT / Organizador': '#286EDD',
  'Jugador': '#74B567',
  'Aficionado / Porra': '#CB9D46',
};
const EMPTY_COLOR = '#888888';

/** Items para la simbología/leyenda del mapa (mismo orden que se muestra) */
export const HEATMAP_LEGEND_ITEMS = [
  { label: 'DT / Organizador', color: PROFILE_COLORS['DT / Organizador'] },
  { label: 'Jugador', color: PROFILE_COLORS['Jugador'] },
  { label: 'Aficionado / Porra', color: PROFILE_COLORS['Aficionado / Porra'] },
  { label: 'Sin datos', color: EMPTY_COLOR },
];

const HeatmapMexicoMap = ({ data = [] }) => {
  const [hoverInfo, setHoverInfo] = useState({
    visible: false,
    x: 0,
    y: 0,
    stateName: '',
    countByProfile: {},
  });

  const states = useMemo(() => {
    return topojson.feature(mexicoTopo, mexicoTopo.objects.states).features;
  }, []);

  const { pathGenerator } = useMemo(() => {
    const w = 500;
    const h = 375;
    const projection = d3Geo.geoMercator();
    projection.fitSize([w, h], { type: 'FeatureCollection', features: states });
    const path = d3Geo.geoPath().projection(projection);
    return { pathGenerator: path };
  }, [states]);

  const getFillColor = (leadingProfile) => {
    if (!leadingProfile) return EMPTY_COLOR;
    return PROFILE_COLORS[leadingProfile] || EMPTY_COLOR;
  };

  const handleMouseMove = (e, stateName, countByProfile) => {
    const bounds = e.currentTarget.closest('svg').getBoundingClientRect();
    setHoverInfo({
      visible: true,
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
      stateName,
      countByProfile: countByProfile || {},
    });
  };

  const handleMouseOut = () => {
    setHoverInfo(prev => ({ ...prev, visible: false }));
  };

  return (
    <MapContainer>
      <SvgMap viewBox="0 0 500 375" preserveAspectRatio="xMidYMid meet">
        {states.map((feature, i) => {
          const stateCode = feature.properties.state_code;
          const stateName = feature.properties.state_name;
          const dataItem = data.find(
            d => Number(d.stateId) === Number(stateCode) || (d.stateName && d.stateName === stateName)
          );
          const leadingProfile = dataItem?.leadingProfile || null;
          const countByProfile = dataItem?.countByProfile || {};
          const fillColor = getFillColor(leadingProfile);

          return (
            <StatePath
              key={i}
              d={pathGenerator(feature)}
              fill={fillColor}
              onMouseMove={(e) => handleMouseMove(e, stateName, countByProfile)}
              onMouseOut={handleMouseOut}
            />
          );
        })}
      </SvgMap>
      <Tooltip $visible={hoverInfo.visible} $x={hoverInfo.x} $y={hoverInfo.y}>
        <div className="state-name">{hoverInfo.stateName}</div>
        <div className="profiles-list">
          {Object.entries(hoverInfo.countByProfile).map(([profile, count]) => (
            <div key={profile} className="profile-row">
              {profile}: {Number(count).toLocaleString()}
            </div>
          ))}
          {Object.keys(hoverInfo.countByProfile).length === 0 && (
            <div className="profile-row">Sin datos</div>
          )}
        </div>
      </Tooltip>
    </MapContainer>
  );
};

export default HeatmapMexicoMap;
